/** @jsx jsx */

import { Colors, Icon, TooltipWrapper } from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import Link from 'app/common/components/Link';
import StatusLabel from 'app/developer-v3/components/StatusLabel';
import { GUIDE_SECTION_IDS } from 'app/developer-v3/components/SidebarRight/constants';
import {
  TABS as SIDEBAR_RIGHT_TABS,
  MODES as SIDEBAR_RIGHT_MODES,
  useSidebarRight,
} from 'app/developer-v3/components/SidebarRight';
import {
  VersionLifecycle,
  VersionLifecycleStatus,
} from 'app/developer-v3/types/statuses';
import { toSentenceCase } from 'app/developer-v3/utils/text';

const Styles = {
  footer: css`
    align-items: center;
    display: flex;
    border-top: 1px solid ${Colors.GrayWarm4};
    padding: 10px;
    justify-content: center;
    width: 340px;
  `,
  popoverContent: css`
    min-width: 250px;
    max-width: 300px;
  `,
  popoverWrapper: css`
    margin-left: 5px;
    cursor: pointer;
  `,
  statusWrapper: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
};

type Props = {
  usersExceedLimit: boolean;
  versionLifecycle: VersionLifecycle;
};

const Status = ({ usersExceedLimit, versionLifecycle }: Props) => {
  const { update: updateSidebarRight } = useSidebarRight();
  const openVersionHelp = () =>
    updateSidebarRight({
      mode: SIDEBAR_RIGHT_MODES.DEFAULT,
      tabActive: SIDEBAR_RIGHT_TABS.GUIDE,
      activeSubsection: GUIDE_SECTION_IDS.VERSIONS,
    });
  const isPromoted =
    versionLifecycle.status === VersionLifecycleStatus.promoted;

  return (
    <div css={Styles.statusWrapper}>
      <StatusLabel
        label={toSentenceCase(versionLifecycle.status)}
        variant={isPromoted ? 'success' : undefined}
      />
      {(isPromoted || usersExceedLimit) && (
        <div
          aria-label="Integration cannot be edited"
          css={Styles.popoverWrapper}
        >
          <TooltipWrapper
            allowMultilineTooltip={true}
            content={
              <div css={Styles.popoverContent}>
                {usersExceedLimit ? (
                  <p>
                    This version is {versionLifecycle.status} and{' '}
                    <strong>has more than 5 active users</strong> and therefore
                    cannot be edited.
                  </p>
                ) : (
                  <p>
                    This version is promoted and therefore cannot be edited.
                  </p>
                )}
                <strong>
                  <Link color="secondary" onClick={openVersionHelp}>
                    How to make modifications to a public app
                  </Link>
                </strong>
              </div>
            }
            position="southeast"
          >
            {({ childProps }) => (
              <Icon
                aria-label="Locked"
                name="actionLockFill"
                size={24}
                {...childProps}
              />
            )}
          </TooltipWrapper>
        </div>
      )}
    </div>
  );
};

export default Status;
